import useToaster from "./useToaster";

export default function useContent() {
  const { addToaster } = useToaster();
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const url = {
    content: `${baseUrl}/api/content`,
    sites: `${baseUrl}/api/site`,
    platform: `${baseUrl}/api/platform`,
    storehouse: `${baseUrl}/api/storehouse`,
    auth: `${baseUrl}/api/auth`,
    queue: `${baseUrl}/api/queue`,
    reshet: `${baseUrl}/api/reshet`,
    features: `${baseUrl}/api/features`,
    users: `${baseUrl}/api/users`,
    me: `${baseUrl}/api/me`,
    statistics: `${baseUrl}/api/statistics`,
    settings: `${baseUrl}/api/settings`,
  };

  const safeFetch = async (endpoint, options = {}) => {
    try {
      const token = localStorage.getItem("x-token");
      const headers = {};

      if (!(options.body instanceof FormData))
        headers["Content-Type"] = "application/json";
      headers["Authorization"] = `Bearer ${token}`;

      if (options.headers) {
        Object.assign(headers, options.headers);
      }

      const response = await fetch(endpoint, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        // App's auth-logout handler clears the session and flips to <Login>.
        window.dispatchEvent(new Event("auth-logout"));
        // A 401 body is empty, so the response.json() below throws a cryptic
        // "Unexpected end of JSON input". Bail cleanly; the redirect takes over.
        throw new Error("Session expired — please log in again");
      }
      if (response && response.error) {
        console.error("API Error:", response.error);
        throw new Error(response.error);
      }
      return await response.json();
    } catch (error) {
      console.error("API request failed:", error.message);
      addToaster({
        message: `${error?.message || JSON.stringify(error)}`,
        severity: "error",
      });
      throw error;
    }
  };

  // Auth API
  const login = async (form) => {
    const { email, password } = form;
    const response = await fetch(url.auth, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ identifier: email, password }),
    });
    if (response.status === 200) {
      const data = await response.json();
      return data;
    } else {
      const error = await response.json();
      return {
        error: error.message,
      };
    }
  };

  // Content API
  const listContent = ({
    page = 1,
    pageSize = 25,
    search = "",
    sources = [],
    tags = [],
    published,
  } = {}) => {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    if (search) qs.set("search", search);
    if (sources?.length) qs.set("source", sources.join(","));
    // Repeated ?tags= entries — tag names may contain commas, so don't join.
    tags?.forEach((t) => qs.append("tags", t));
    if (published === true) qs.set("published", "true");
    else if (published === false) qs.set("published", "false");
    return safeFetch(`${url.content}?${qs.toString()}`);
  };

  const recordDownload = (id) =>
    safeFetch(`${url.content}/${id}/download`, { method: "POST" });

  const setPublished = (id, published) =>
    safeFetch(`${url.content}/${id}/published`, {
      method: "PATCH",
      body: JSON.stringify({ published }),
    });

  const getContentById = (id) => safeFetch(`${url.content}/${id}`);

  const getTagVocabulary = () => safeFetch(`${url.content}/tags`);

  const getContentDownloads = (id) =>
    safeFetch(`${url.content}/${id}/downloads`);

  const createContent = ({
    title,
    tags,
    type,
    content,
    source,
    metadata,
    banner,
    description,
  }) =>
    safeFetch(url.content, {
      method: "POST",
      body: JSON.stringify({
        title,
        tags,
        type,
        content,
        source,
        metadata,
        banner,
        description,
      }),
    });

  const generateContent = ({
    prompt,
    lang,
    type,
    minLength,
    maxLength,
    maxSources,
    noImages,
  }) =>
    safeFetch(`${url.content}/generate`, {
      method: "POST",
      body: JSON.stringify({
        prompt,
        lang,
        type,
        minLength,
        maxLength,
        maxSources,
        noImages,
      }),
    });

  const generateFromUrl = ({ url: articleUrl, geoReport }) =>
    safeFetch(`${url.content}/generate-from-url`, {
      method: "POST",
      body: JSON.stringify({ url: articleUrl, geoReport }),
    });

  const generateVideoContent = ({ files, paragraphs }) => {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    if (paragraphs !== undefined) formData.append("paragraphs", paragraphs);
    return safeFetch(`${url.content}/generate-video`, {
      method: "POST",
      body: formData,
    });
  };

  const updateContent = (id, content) =>
    safeFetch(`${url.content}/${id}`, {
      method: "PUT",
      body: JSON.stringify({ content: { id: id, ...content } }),
    });

  const deleteContent = (id) =>
    safeFetch(`${url.content}/${id}`, { method: "DELETE" });

  // One turn of the in-editor assistant. `markdown` is the live editor buffer,
  // so the returned anchors line up with unsaved edits.
  const assistArticle = async (
    id,
    { markdown, message, selection, uiLanguage, history }
  ) => {
    const result = await safeFetch(`${url.content}/${id}/assistant`, {
      method: "POST",
      body: JSON.stringify({ markdown, message, selection, uiLanguage, history }),
    });
    // safeFetch doesn't check response.ok, so a 4xx/5xx body arrives here as
    // { message } instead of throwing. The panel renders the failure inline.
    if (typeof result?.reply !== "string")
      throw new Error(result?.message || "Assistant request failed");
    return result;
  };

  // Sites API
  const listSites = () => safeFetch(url.sites);

  const getSite = (id) => safeFetch(`${url.sites}/${id}`);

  const updateSite = (id, site) =>
    safeFetch(`${url.sites}/${id}`, {
      method: "PUT",
      body: JSON.stringify(site),
    });

  const bindSite = (siteName, contentId) =>
    safeFetch(`${url.sites}/${siteName}`, {
      method: "POST",
      body: JSON.stringify({ id: contentId }),
    });

  const republishSite = (id, site) =>
    safeFetch(`${url.sites}/${id}/republish`, {
      method: "PATCH",
      body: JSON.stringify(site),
    });

  const setFeatured = (siteName, contentId) =>
    safeFetch(`${url.sites}/${siteName}/featured`, {
      method: "PATCH",
      body: JSON.stringify({ id: contentId }),
    });

  // Platform API
  const createPage = (name, id) =>
    safeFetch(url.platform, {
      method: "POST",
      body: JSON.stringify({ id, name }),
    });

  const deletePage = (id, name) =>
    safeFetch(url.platform, {
      method: "DELETE",
      body: JSON.stringify({ id, name }),
    });

  // Storehouse API
  const listStorehouse = () => safeFetch(url.storehouse);

  const getStorehouse = (name) => safeFetch(`${url.storehouse}/${name}`);

  const uploadStorehouse = (files) => {
    const formData = new FormData();
    for (let file of files) formData.append("files", file);
    return safeFetch(url.storehouse, {
      method: "POST",
      body: formData,
    });
  };

  const updateStorehouse = (name, data) =>
    safeFetch(`${url.storehouse}/${name}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

  const deleteStorehouse = (name) =>
    safeFetch(`${url.storehouse}/${name}`, {
      method: "DELETE",
    });

  const sync = () =>
    safeFetch(url.storehouse, {
      method: "PATCH",
    });

  // Features API
  const getReframeEditData = (id) =>
    safeFetch(`${url.features}/reframe/${id}/edit-data`);

  const updateReframeEditData = (id, data) =>
    safeFetch(`${url.features}/reframe/${id}/edit-data`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

  const reRenderReframe = (id, overlay) =>
    safeFetch(`${url.features}/reframe/${id}/re-render`, {
      method: "POST",
      body: JSON.stringify(overlay ? { overlay } : {}),
    });

  const applyOverlayOnly = (id, overlay) =>
    safeFetch(`${url.features}/reframe/${id}/apply-overlay`, {
      method: "POST",
      body: JSON.stringify({ overlay }),
    });

  const getReRenderStatus = (id, jobId) =>
    safeFetch(`${url.features}/reframe/${id}/re-render/status/${jobId}`);

  const reframeVideo = ({ name, file, overlay }) => {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("type", "reframer");
    formData.append("file", file);
    if (overlay) formData.append("overlay", JSON.stringify(overlay));
    return safeFetch(`${url.features}/reframe-video`, {
      method: "POST",
      body: formData,
    });
  };

  // Queue API
  const getQueue = ({ status, pageSize } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (pageSize) qs.set("pageSize", String(pageSize));
    const q = qs.toString();
    return safeFetch(`${url.queue}/${q ? `?${q}` : ""}`);
  };
  const getQueueSourceVideoUrl = (id) =>
    safeFetch(`${url.queue}/${id}/source-video`);
  const retryQueueItem = (id) =>
    safeFetch(`${url.queue}/${id}/retry`, { method: "POST" });

  // Reshet API
  const getReshetRecords = (timezone) =>
    safeFetch(
      timezone !== undefined
        ? `${url.reshet}/?timezone=${timezone}`
        : `${url.reshet}/`
    );

  // Accepts a oneshot ({ name/label, description, startTime, endTime }) or a
  // recurring ({ type: "recurring", label, description, days, startLocal, durationMin }).
  const createReshetRecord = (payload) =>
    safeFetch(url.reshet, {
      method: "POST",
      body: JSON.stringify(payload),
    });

  const deleteReshetRecord = (id) =>
    safeFetch(`${url.reshet}/${id}`, { method: "DELETE" });

  const getTVGuide = () => safeFetch(`${url.reshet}/tvguide`);

  // Resolved recording plan for the visible week (show-anchored, drift/rename-aware).
  // `days` scopes how far forward the plan goes (1..7, default the full week). Pass 1
  // when you only need today — the server then does a single persisted read instead of
  // dry-run-matching the six future days against the TV guide (which can hit Gemini).
  const getRecordingPlan = (days) =>
    safeFetch(`${url.reshet}/plan${days ? `?days=${days}` : ""}`);

  // Settings API (admin)
  const getRecordingSettings = () => safeFetch(`${url.settings}/recording`);

  const updateRecordingSettings = ({ padMinutes, clipPadSeconds, guideRefreshMinutes }) =>
    safeFetch(`${url.settings}/recording`, {
      method: "PUT",
      body: JSON.stringify({ padMinutes, clipPadSeconds, guideRefreshMinutes }),
    });

  // { settings: { <feature>: modelId }, features: [{ key, default, catalog }] }
  const getModelSettings = () => safeFetch(`${url.settings}/models`);

  const updateModelSettings = (patch) =>
    safeFetch(`${url.settings}/models`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });

  // Users API (admin)
  const listUsers = () => safeFetch(url.users);

  const getUser = (id) => safeFetch(`${url.users}/${id}`);

  const createUser = ({ name, email, password, role, active, group }) =>
    safeFetch(url.users, {
      method: "POST",
      body: JSON.stringify({ name, email, password, role, active, group }),
    });

  const updateUser = (id, payload) =>
    safeFetch(`${url.users}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });

  const setUserPassword = (id, password) =>
    safeFetch(`${url.users}/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });

  // Statistics API (admin)
  const getStatistics = ({ from, to, userId, source, excludeAdmins } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    if (userId) qs.set("userId", userId);
    if (source) qs.set("source", source);
    if (excludeAdmins) qs.set("excludeAdmins", excludeAdmins);
    const q = qs.toString();
    return safeFetch(`${url.statistics}${q ? `?${q}` : ""}`);
  };

  // Me API
  const getMe = () => safeFetch(url.me);

  const updateMe = ({ name }) =>
    safeFetch(url.me, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });

  const changeMyPassword = ({ currentPassword, newPassword }) =>
    safeFetch(`${url.me}/password`, {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });

  return {
    //auth
    login,
    //content
    listContent,
    createContent,
    getContentById,
    getTagVocabulary,
    generateContent,
    generateFromUrl,
    generateVideoContent,
    updateContent,
    deleteContent,
    assistArticle,
    recordDownload,
    setPublished,
    getContentDownloads,
    //sites
    listSites,
    getSite,
    updateSite,
    bindSite,
    setFeatured,
    republishSite,
    //platform
    createPage,
    deletePage,
    //storehouse
    listStorehouse,
    getStorehouse,
    uploadStorehouse,
    updateStorehouse,
    deleteStorehouse,
    sync,
    //features
    reframeVideo,
    getReframeEditData,
    updateReframeEditData,
    reRenderReframe,
    applyOverlayOnly,
    getReRenderStatus,
    //queue
    getQueue,
    getQueueSourceVideoUrl,
    retryQueueItem,
    //reshet
    getReshetRecords,
    getRecordingPlan,
    createReshetRecord,
    deleteReshetRecord,
    getTVGuide,
    //settings
    getRecordingSettings,
    updateRecordingSettings,
    getModelSettings,
    updateModelSettings,
    //users
    listUsers,
    getUser,
    createUser,
    updateUser,
    setUserPassword,
    //me
    getMe,
    updateMe,
    changeMyPassword,
    //statistics
    getStatistics,
  };
}
